import type { CanvasItem, DrawingClientEvent, StrokeItem } from "./types";

/**
 * Pure reducer for applying drawing commands to an ordered item list. Used by
 * BOTH the server (CanvasHistory, the canonical log) and the client
 * (CanvasRenderer, mirroring that log locally for instant/optimistic
 * rendering) so the two can never drift apart - see ARCHITECTURE.md.
 */
export interface CanvasReducerState {
  items: CanvasItem[];
  redoStack: CanvasItem[];
  inProgress: Map<string, StrokeItem>;
}

/**
 * "One Line Man" auto-clears the canvas immediately before any 2nd+ stroke. That
 * clear is conceptually paired with the stroke that triggered it - undoing the
 * stroke should also undo the clear (revealing the PREVIOUS stroke), not leave an
 * orphaned clear that just blanks the canvas. Both the server (which generates the
 * clear) and each client (which predicts its own local copy so the artist's own
 * view doesn't lag behind - see CanvasEditor.ts) tag it with this id prefix, so
 * pairing can be recognized purely from the id with no extra bookkeeping to keep
 * in sync.
 */
export const ONE_LINE_MAN_CLEAR_ID_PREFIX = "olm-auto-clear-";

export function isAutoClearItem(item: CanvasItem | undefined): boolean {
  return !!item && item.kind === "clear" && item.id.startsWith(ONE_LINE_MAN_CLEAR_ID_PREFIX);
}

export function createCanvasReducerState(): CanvasReducerState {
  return { items: [], redoStack: [], inProgress: new Map() };
}

export function resetCanvasReducerState(state: CanvasReducerState): void {
  state.items = [];
  state.redoStack = [];
  state.inProgress.clear();
}

export function applyCanvasEvent(state: CanvasReducerState, event: DrawingClientEvent, timestamp: number): void {
  switch (event.type) {
    case "strokeStart": {
      const stroke: StrokeItem = {
        kind: "stroke",
        id: event.id,
        tool: event.tool,
        color: event.color,
        size: event.size,
        points: [event.point],
        timestamp,
      };
      state.items.push(stroke);
      state.inProgress.set(event.id, stroke);
      state.redoStack = [];
      break;
    }
    case "strokePoint": {
      state.inProgress.get(event.id)?.points.push(event.point);
      break;
    }
    case "strokePoints": {
      const stroke = state.inProgress.get(event.id);
      if (!stroke) break;
      for (let i = 0; i + 1 < event.points.length; i += 2) {
        stroke.points.push({ x: event.points[i]!, y: event.points[i + 1]! });
      }
      break;
    }
    case "strokeEnd": {
      state.inProgress.delete(event.id);
      break;
    }
    case "fill": {
      state.items.push({ kind: "fill", id: event.id, point: event.point, color: event.color, timestamp });
      state.redoStack = [];
      break;
    }
    case "clear": {
      state.items.push({ kind: "clear", id: event.id, timestamp });
      state.redoStack = [];
      break;
    }
    case "undo": {
      const popped = state.items.pop();
      if (popped) state.redoStack.push(popped);
      while (isAutoClearItem(state.items[state.items.length - 1])) {
        state.redoStack.push(state.items.pop()!);
      }
      break;
    }
    case "redo": {
      let restored = state.redoStack.pop();
      if (restored) state.items.push(restored);
      while (restored && isAutoClearItem(restored)) {
        restored = state.redoStack.pop();
        if (restored) state.items.push(restored);
      }
      break;
    }
  }
}
